// frontend/src/hooks/usePromotions.js
//
// Read query + list-action mutation hooks for the Promotions resource: the
// paginated list query, one promotion's items, plus delete/apply mutations. Create and update live in
// usePromotionDraft, which saves through sessionApi outside TanStack and
// announces the change itself.

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { DATA } from '../queries/dataChanged';
import { promotions as promotionsKey } from '../queries/keys';
import { apiJson, readResult } from '../queries/api';
import { sessionApi } from '../services/SessionApiService';
import { fetchAllPagesVia } from '../utils/fetchAllPages';
import { useApiMutation } from '../queries/useApiMutation';

const API = '/api/promotions/';
const MEMBERS_API = '/api/promotion-items/';
const THIRTY_SEC = 30 * 1000;

/**
 * Read hook — every promotion of the active store. The page filters by
 * status itself, on the status each row shows (utils/promotionStatus.js).
 */
export function usePromotions() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;

  const query = useQuery({
    queryKey: promotionsKey.list(storeId),
    queryFn: () => fetchAllPagesVia(sessionApi, API, { label: 'promotions' }),
    staleTime: THIRTY_SEC,
    enabled: !!storeId,
  });

  const { data: promotions, ...result } = readResult(query, t);
  return { promotions, ...result };
}

/**
 * Read hook — the first page of one promotion's items, as
 * `{ count, results }` (api/views/promotions.py PromotionItemViewSet):
 * `count` is every item the promotion has, `results` the ones on the page.
 */
export function usePromotionMembers(promotionId) {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;

  const query = useQuery({
    queryKey: promotionsKey.members(storeId, promotionId),
    queryFn: () => apiJson.get(`${MEMBERS_API}?promotion=${promotionId}`),
    staleTime: THIRTY_SEC,
    enabled: !!storeId,
  });

  const { data: members, ...result } = readResult(query, t);
  return { members, ...result };
}

/**
 * Delete a promotion. Optimistic: the row vanishes from the cached list
 * immediately and is restored on error. The canonical list
 * still resyncs from the backend afterwards (covers cascade effects like
 * associated discount cleanup).
 */
export function useDeletePromotion() {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;

  return useApiMutation({
    mutationFn: (id) => apiJson.delete(`${API}${id}/`),
    // Deleting a promotion that is not a draft takes its lift off the
    // forecast rows, and a live one puts the items' sell prices back.
    changes: [DATA.promotions, DATA.forecasts, DATA.items],
    optimistic: {
      cacheKey: promotionsKey.all(storeId),
      apply: (qc, id) => {
        qc.setQueriesData(
          { queryKey: promotionsKey.all(storeId) },
          (data) => Array.isArray(data) ? data.filter((p) => p.PromotionID !== id) : data,
        );
      },
    },
  });
}

/**
 * Apply a draft promotion (transitions status). Not optimistic — the
 * server-side transition has side effects (Shopify price push, demand
 * recalc) that we want the merchant to see authoritatively, not preview;
 * the write names the items and forecasts it moves.
 *
 * A large all-items (or large supplier/category) price promo is gated behind
 * a blast-radius confirmation: the backend returns 409
 * `price_confirmation_required` until the request carries `confirmed: true`.
 * The gate does no work and the price push is async either way, so confirming
 * re-sends immediately. Callers pass `{ id, confirmed }` — see the confirm
 * dialog in Promotions.jsx.
 */
export function useApplyPromotion() {
  return useApiMutation({
    mutationFn: ({ id, confirmed = false }) =>
      apiJson.post(`${API}${id}/apply/`, confirmed ? { confirmed: true } : {}),
    changes: [DATA.promotions, DATA.items, DATA.forecasts],
  });
}
